import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify, usePermissions, useTranslate } from 'react-admin';
import {
  BbLbBoardCatalogEntry,
  BbLbPhase,
  BbLbPhaseBoard,
  bbLbAddPhaseBoard,
  bbLbApplyTiersToAll,
  bbLbClonePhaseBoards,
  bbLbGetPhaseBoards,
  bbLbGetPhaseTiers,
  bbLbRemovePhaseBoard,
  bbLbReplacePhaseTiers,
  bbLbUpdatePhaseBoard,
} from '../../../services/blindBoxService';
import { PermissionData } from '../../../entities/permission.entity';
import { hasPermission } from '../../../utils/has-permission';
import { STATUS_META, boardLabel, catalogLabel, decimalOnly, digitsOnly } from './helpers';

type TierRow = {
  id?: string;
  minRank: string;
  maxRank: string;
  usdc: string;
  box: string;
  autoPayout: boolean;
};

const emptyTierRow = (): TierRow => ({
  minRank: '',
  maxRank: '',
  usdc: '',
  box: '',
  autoPayout: true,
});

/** Stable signature of the editable tier fields — used to detect unsaved changes. */
const serializeTiers = (rows: TierRow[]) =>
  JSON.stringify(rows.map((r) => [r.minRank, r.maxRank, r.usdc, r.box, r.autoPayout]));

const catalogKey = (source: string, metric: string) => `${source}:${metric}`;

/** Grid columns shared by the board-list header and each sortable row's header. */
const BOARD_GRID = '30px minmax(200px, 1.8fr) 90px 90px 40px 48px';

// ── Sortable board row — expand to edit its tiers; drag to reorder; enabled auto-saves ──

function SortableBoardRow({
  board,
  expanded,
  saving,
  canEdit,
  onToggle,
  onEnabled,
  onRemove,
  children,
}: {
  board: BbLbPhaseBoard;
  expanded: boolean;
  saving: boolean;
  canEdit: boolean;
  onToggle: () => void;
  onEnabled: (board: BbLbPhaseBoard, next: boolean) => void;
  onRemove: (board: BbLbPhaseBoard) => void;
  children: ReactNode;
}) {
  const t = useTranslate();
  const { permissions } = usePermissions<PermissionData>();
  const permissionList = permissions?.permissions;
  // PUT /blind-box/leaderboard/phase-boards/:phaseBoardId requires bb-leaderboard:update
  const canUpdateBoard = hasPermission(permissionList, 'bb-leaderboard', 'update');
  // DELETE /blind-box/leaderboard/phase-boards/:phaseBoardId requires bb-leaderboard:delete
  const canDeleteBoard = hasPermission(permissionList, 'bb-leaderboard', 'delete');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: board.id,
    disabled: !canEdit || !canUpdateBoard,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Interacting with a control shouldn't also toggle the row.
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: expanded ? 'action.selected' : 'background.paper',
        boxShadow: expanded ? (theme) => `inset 3px 0 0 ${theme.palette.primary.main}` : 'none',
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          display: 'grid',
          gridTemplateColumns: BOARD_GRID,
          gap: 1,
          p: 1,
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': { bgcolor: expanded ? 'action.selected' : 'action.hover' },
        }}
      >
        <IconButton
          size="small"
          disabled={!canEdit || !canUpdateBoard}
          sx={{ cursor: canEdit && canUpdateBoard ? 'grab' : 'default', color: 'text.disabled' }}
          onClick={stop}
          {...attributes}
          {...listeners}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" noWrap title={boardLabel(board, t)}>
          {boardLabel(board, t)}
        </Typography>
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          onClick={stop}
        >
          <Switch
            size="small"
            checked={!!board.enabled}
            disabled={saving || !canUpdateBoard}
            onChange={(e) => onEnabled(board, e.target.checked)}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          {t('module.blindBox.lbTiersN', { count: board.tierCount ?? 0 })}
        </Typography>
        <Tooltip title={t('module.blindBox.lbEditTiers')} arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              stop(e);
              onToggle();
            }}
            sx={{
              width: 32,
            }}
          >
            <ExpandMoreIcon
              fontSize="small"
              sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('actions.delete')} arrow>
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={saving || !canEdit || !canDeleteBoard}
              onClick={(e) => {
                stop(e);
                onRemove(board);
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'action.hover', p: 2 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

// ── Reward config tab ─────────────────────────────────────────────────────────

export function RewardTab({
  phases,
  catalog,
}: {
  phases: BbLbPhase[];
  catalog: BbLbBoardCatalogEntry[];
}) {
  const t = useTranslate();
  const notify = useNotify();

  const [phaseId, setPhaseId] = useState('');
  const [boards, setBoards] = useState<BbLbPhaseBoard[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [savingBoardId, setSavingBoardId] = useState<string | null>(null);

  // Catalog key currently being added ('' = none). Reward currency is set per tier band, not on the
  // board, so adding only needs the (source, metric) pair.
  const [addingKey, setAddingKey] = useState('');

  // Clone-from-phase state.
  const [cloneFromId, setCloneFromId] = useState('');
  const [cloning, setCloning] = useState(false);

  const { permissions } = usePermissions<PermissionData>();
  const permissionList = permissions?.permissions;
  // Leaderboard phase-board / tier writes: POST = create, PUT / apply-to-all = update
  const canCreateLb = hasPermission(permissionList, 'bb-leaderboard', 'create');
  const canUpdateLb = hasPermission(permissionList, 'bb-leaderboard', 'update');

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // Default the phase to the first upcoming one (config target), else the newest.
  useEffect(() => {
    if (phaseId || !phases.length) return;
    const upcoming = phases.find((p) => p.status === 'upcoming');
    setPhaseId((upcoming ?? phases[0]).id);
  }, [phases, phaseId]);

  const selectedPhase = useMemo(() => phases.find((p) => p.id === phaseId), [phases, phaseId]);
  const canEditTiers = selectedPhase?.status === 'upcoming';
  // UI restriction: structural board changes (add / remove / reorder) are limited to upcoming phases.
  // The enabled switch stays live — hiding a board mid-phase is its whole purpose.
  const canEditBoards = selectedPhase?.status === 'upcoming';

  const reloadBoards = useCallback(() => {
    if (!phaseId) {
      setBoards([]);
      return;
    }
    setLoadingBoards(true);
    bbLbGetPhaseBoards(phaseId)
      .then((rows) =>
        setBoards([...rows].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)))
      )
      .catch((e: any) => {
        notify(e?.message || 'Error', { type: 'error' });
        setBoards([]);
      })
      .finally(() => setLoadingBoards(false));
  }, [phaseId, notify]);

  useEffect(() => {
    reloadBoards();
  }, [reloadBoards]);

  // Catalog entries not yet added to this phase.
  const availableEntries = useMemo(() => {
    const taken = new Set(boards.map((b) => catalogKey(b.source, b.metric)));
    return catalog.filter((e) => !taken.has(catalogKey(e.source, e.metric)));
  }, [catalog, boards]);

  const persistBoard = async (id: string, patch: Parameters<typeof bbLbUpdatePhaseBoard>[1]) => {
    setSavingBoardId(id);
    try {
      await bbLbUpdatePhaseBoard(id, patch);
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      reloadBoards();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
      reloadBoards();
    } finally {
      setSavingBoardId(null);
    }
  };

  const onEnabled = (board: BbLbPhaseBoard, next: boolean) =>
    persistBoard(board.id, { enabled: next });

  const onRemove = async (board: BbLbPhaseBoard) => {
    if (!window.confirm(t('module.blindBox.confirmRemoveBoard'))) return;
    setSavingBoardId(board.id);
    try {
      await bbLbRemovePhaseBoard(board.id);
      notify(t('module.blindBox.deleteOk'), { type: 'success' });
      reloadBoards();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setSavingBoardId(null);
    }
  };

  const addBoardEntry = async (entry: BbLbBoardCatalogEntry) => {
    setAddingKey(catalogKey(entry.source, entry.metric));
    try {
      await bbLbAddPhaseBoard(phaseId, {
        source: entry.source,
        metric: entry.metric,
      });
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      reloadBoards();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setAddingKey('');
    }
  };

  const cloneBoards = async () => {
    if (!cloneFromId) {
      notify(t('module.blindBox.lbCloneFromRequired'), { type: 'warning' });
      return;
    }
    if (!window.confirm(t('module.blindBox.lbCloneConfirm'))) return;
    setCloning(true);
    try {
      await bbLbClonePhaseBoards({ fromPhaseId: cloneFromId, toPhaseId: phaseId });
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setCloneFromId('');
      reloadBoards();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setCloning(false);
    }
  };

  const handleBoardDragEnd = (e: DragEndEvent) => {
    if (!canEditBoards) return;
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = boards.findIndex((b) => b.id === active.id);
    const newIndex = boards.findIndex((b) => b.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(boards, oldIndex, newIndex);
    setBoards(next); // optimistic
    const changed = next.filter((b, i) => (b.displayOrder ?? 0) !== i);
    if (!changed.length) return;
    setSavingBoardId('*');
    Promise.all(
      next.map((b, i) =>
        (b.displayOrder ?? 0) !== i ? bbLbUpdatePhaseBoard(b.id, { displayOrder: i }) : null
      )
    )
      .then(() => {
        notify(t('module.blindBox.saveOk'), { type: 'success' });
        reloadBoards();
      })
      .catch((err: any) => {
        notify(err?.message || 'Error', { type: 'error' });
        reloadBoards();
      })
      .finally(() => setSavingBoardId(null));
  };

  // ── Tier editor — targets whichever board is expanded (accordion, one at a time) ──────
  const [expandedId, setExpandedId] = useState('');
  const [tiers, setTiers] = useState<TierRow[]>([]);
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [savingTiers, setSavingTiers] = useState(false);
  const [tiersBaseline, setTiersBaseline] = useState('[]');

  // Collapse if the expanded board is no longer in the current phase.
  useEffect(() => {
    if (expandedId && !boards.some((b) => b.id === expandedId)) setExpandedId('');
  }, [boards, expandedId]);

  const tiersDirty = serializeTiers(tiers) !== tiersBaseline;

  const loadTiers = useCallback(() => {
    if (!expandedId) {
      setTiers([]);
      setTiersBaseline(serializeTiers([]));
      return;
    }
    setLoadingTiers(true);
    bbLbGetPhaseTiers(expandedId)
      .then((rows) => {
        const mapped: TierRow[] = [...rows]
          .sort((a, b) => (a.minRank ?? 0) - (b.minRank ?? 0))
          .map((r) => ({
            id: r.id,
            minRank: String(r.minRank),
            maxRank: String(r.maxRank),
            usdc: String(r.usdcRewardPerRank ?? ''),
            box: String(r.boxRewardPerRank ?? ''),
            autoPayout: !!r.autoPayout,
          }));
        setTiers(mapped);
        setTiersBaseline(serializeTiers(mapped));
      })
      .catch((e: any) => {
        notify(e?.message || 'Error', { type: 'error' });
        setTiers([]);
        setTiersBaseline(serializeTiers([]));
      })
      .finally(() => setLoadingTiers(false));
  }, [expandedId, notify]);

  useEffect(() => {
    loadTiers();
  }, [loadTiers]);

  const toggleBoard = (id: string) => setExpandedId((cur) => (cur === id ? '' : id));

  const setTier = (idx: number, patch: Partial<TierRow>) =>
    setTiers((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addTier = () => setTiers((prev) => [...prev, emptyTierRow()]);
  const removeTier = (idx: number) => setTiers((prev) => prev.filter((_, i) => i !== idx));

  // A band is valid when: ranks are a positive ascending pair, both reward legs are valid non-negative
  // numbers, and at least one leg is > 0 (a 0/0 band would pay nothing).
  const num = (s: string) => (s.trim() === '' ? 0 : Number(s));
  const validTiers = () => {
    for (let i = 0; i < tiers.length; i += 1) {
      const r = tiers[i];
      const min = parseInt(r.minRank, 10);
      const max = parseInt(r.maxRank, 10);
      if (!Number.isInteger(min) || !Number.isInteger(max) || min < 1 || max < min) return i;
      const usdc = num(r.usdc);
      const box = num(r.box);
      if (!Number.isFinite(usdc) || !Number.isFinite(box) || usdc < 0 || box < 0) return i;
      if (usdc <= 0 && box <= 0) return i;
    }
    return -1;
  };

  const saveTiers = async () => {
    if (!canEditTiers) {
      notify(t('module.blindBox.lbPhaseLockedHint'), { type: 'warning' });
      return;
    }
    const bad = validTiers();
    if (bad >= 0) {
      notify(`#${bad + 1}: ${t('module.blindBox.lbErrRankBand')}`, { type: 'warning' });
      return;
    }
    setSavingTiers(true);
    try {
      await bbLbReplacePhaseTiers(
        expandedId,
        tiers.map((r) => ({
          minRank: parseInt(r.minRank, 10),
          maxRank: parseInt(r.maxRank, 10),
          usdcRewardPerRank: String(num(r.usdc)),
          boxRewardPerRank: String(num(r.box)),
          autoPayout: r.autoPayout,
        }))
      );
      notify(t('module.blindBox.lbTiersSavedOk'), { type: 'success' });
      loadTiers();
      reloadBoards(); // refresh tierCount
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setSavingTiers(false);
    }
  };

  const applyToAll = async () => {
    if (!canEditTiers) {
      notify(t('module.blindBox.lbPhaseLockedHint'), { type: 'warning' });
      return;
    }
    if (!window.confirm(t('module.blindBox.lbApplyToAllConfirm'))) return;
    setSavingTiers(true);
    try {
      await bbLbApplyTiersToAll(expandedId);
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      reloadBoards();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setSavingTiers(false);
    }
  };

  const canClone = selectedPhase?.status === 'upcoming' && boards.length === 0;

  // The tier editor rendered inside the expanded board's panel.
  const renderTierEditor = () => (
    <Stack spacing={1.5}>
      <TableContainer
        sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 70 }}>{t('module.blindBox.lbTier')}</TableCell>
              <TableCell>{t('module.blindBox.lbMinRank')}</TableCell>
              <TableCell>{t('module.blindBox.lbMaxRank')}</TableCell>
              <TableCell>{t('module.blindBox.lbUsdcPerRank')}</TableCell>
              <TableCell>{t('module.blindBox.lbBoxPerRank')}</TableCell>
              <TableCell>{t('module.blindBox.lbAutoPayout')}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingTiers ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('common.loading', { _: 'Loading...' })}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : tiers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('module.blindBox.lbNoTiers')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tiers.map((r, idx) => (
                <TableRow key={idx}>
                  <TableCell sx={{ color: 'text.secondary' }}>{idx + 1}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="standard"
                      value={r.minRank}
                      disabled={!canEditTiers}
                      onChange={(e) => setTier(idx, { minRank: digitsOnly(e.target.value) })}
                      inputProps={{ inputMode: 'numeric' }}
                      sx={{ width: 90, minHeight: 36, '& .MuiInputBase-root': { minHeight: 36 } }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="standard"
                      value={r.maxRank}
                      disabled={!canEditTiers}
                      onChange={(e) => setTier(idx, { maxRank: digitsOnly(e.target.value) })}
                      inputProps={{ inputMode: 'numeric' }}
                      sx={{ width: 90, minHeight: 36, '& .MuiInputBase-root': { minHeight: 36 } }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="standard"
                      value={r.usdc}
                      placeholder="0"
                      disabled={!canEditTiers}
                      onChange={(e) => setTier(idx, { usdc: decimalOnly(e.target.value) })}
                      inputProps={{ inputMode: 'decimal' }}
                      sx={{ width: 120, minHeight: 36, '& .MuiInputBase-root': { minHeight: 36 } }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="standard"
                      value={r.box}
                      placeholder="0"
                      disabled={!canEditTiers}
                      onChange={(e) => setTier(idx, { box: decimalOnly(e.target.value) })}
                      inputProps={{ inputMode: 'decimal' }}
                      sx={{ width: 120, minHeight: 36, '& .MuiInputBase-root': { minHeight: 36 } }}
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={r.autoPayout}
                      disabled={!canEditTiers}
                      onChange={(e) => setTier(idx, { autoPayout: e.target.checked })}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      disabled={!canEditTiers}
                      onClick={() => removeTier(idx)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
        flexWrap="wrap"
        useFlexGap
      >
        <Button
          size="small"
          startIcon={<AddIcon />}
          disabled={!canEditTiers}
          onClick={addTier}
          variant="contained"
          color="primary"
        >
          {t('module.blindBox.lbAddTier')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="contained"
          color="secondary"
          disabled={!canEditTiers || savingTiers || !tiersDirty || !canUpdateLb}
          onClick={saveTiers}
        >
          {t('module.blindBox.lbSaveTiers')}
        </Button>
        <Tooltip title={t('module.blindBox.lbApplyToAllHint')} arrow>
          <span>
            <Button
              size="small"
              variant="contained"
              disabled={!canEditTiers || savingTiers || !canUpdateLb}
              onClick={applyToAll}
            >
              {t('module.blindBox.lbApplyToAll')}
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );

  return (
    <Stack spacing={2}>
      {/* Phase selector + clone */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <TextField
          select
          size="small"
          label={t('module.blindBox.lbPhase')}
          value={phaseId}
          onChange={(e) => setPhaseId(e.target.value)}
          sx={{ minWidth: 240, height: 40, '& .MuiInputBase-root': { minHeight: 40 } }}
        >
          {phases.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name || p.id} · {t(STATUS_META[p.status]?.key ?? 'module.blindBox.lbStatus')}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <TextField
          select
          size="small"
          label={t('module.blindBox.lbCloneFrom')}
          value={cloneFromId}
          onChange={(e) => setCloneFromId(e.target.value)}
          sx={{ minWidth: 200, height: 40, '& .MuiInputBase-root': { minHeight: 40 } }}
        >
          <MenuItem value="">—</MenuItem>
          {phases
            .filter((p) => p.id !== phaseId)
            .map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name || p.id}
              </MenuItem>
            ))}
        </TextField>
        <Tooltip title={t('module.blindBox.lbCloneHint')} arrow>
          <span>
            <Button
              size="small"
              variant="contained"
              disabled={cloning || !cloneFromId || !canClone || !canCreateLb}
              onClick={cloneBoards}
              sx={{ height: 40, px: 1.5 }}
            >
              {t('module.blindBox.lbCloneBoards')}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {/* Boards for this phase — expand a row to edit its reward tiers */}
      <Box>
        {!canEditBoards ? (
          <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
            {t('module.blindBox.lbBoardsLockedHint')}
          </Alert>
        ) : null}

        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: BOARD_GRID,
              gap: 1,
              p: 1,
              alignItems: 'center',
              bgcolor: 'background.neutral',
            }}
          >
            <span />
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {t('module.blindBox.lbBoard')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={600}
              sx={{ textAlign: 'center' }}
            >
              {t('module.blindBox.lbRewardEnabled')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={600}
              sx={{ textAlign: 'center' }}
            >
              {t('module.blindBox.lbRewardTiers')}
            </Typography>
            <span />
            <span />
          </Box>
          {loadingBoards ? (
            <Box sx={{ px: 1, py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('common.loading', { _: 'Loading...' })}
              </Typography>
            </Box>
          ) : boards.length === 0 && availableEntries.length === 0 ? (
            <Box sx={{ px: 1, py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('module.blindBox.lbNoBoards')}
              </Typography>
            </Box>
          ) : (
            <>
              {boards.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis]}
                  onDragEnd={handleBoardDragEnd}
                >
                  <SortableContext
                    items={boards.map((b) => b.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {boards.map((b) => (
                      <SortableBoardRow
                        key={b.id}
                        board={b}
                        expanded={b.id === expandedId}
                        saving={savingBoardId === b.id || savingBoardId === '*'}
                        canEdit={canEditBoards}
                        onToggle={() => toggleBoard(b.id)}
                        onEnabled={onEnabled}
                        onRemove={onRemove}
                      >
                        {b.id === expandedId ? renderTierEditor() : null}
                      </SortableBoardRow>
                    ))}
                  </SortableContext>
                </DndContext>
              ) : null}
              {/* Catalog boards not yet in this phase — badge + inline add */}
              {availableEntries.map((e) => {
                const key = catalogKey(e.source, e.metric);
                return (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1,
                      py: 1,
                      borderTop: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ width: 30, flexShrink: 0 }} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      {catalogLabel(e, t)}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!canEditBoards || addingKey === key || !canCreateLb}
                      onClick={() => addBoardEntry(e)}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {t('module.blindBox.lbAddToPhase', {
                        phase: selectedPhase?.name || selectedPhase?.id || '',
                      })}
                    </Button>
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      </Box>
    </Stack>
  );
}
